export interface FaqRefType {
	label: string
	/** 내부 라우트, 또는 mailto: */
	href: string
}

export interface FaqItemType {
	no: string
	/** 왼쪽 열 키커. 같은 값이 이어지면 한 묶음으로 읽힌다 */
	group: string
	question: string
	/** 문단 단위. 한 문단은 세 문장을 넘기지 않는다 */
	answer: string[]
	refs?: FaqRefType[]
}

/**
 * FAQ 10문항 — judahsWeb/SITEMAP_PLAN.md §23.
 *
 * 문항은 추측이 아니라 운영 DB 의 문의 78건(2025-02 ~ 2026-09)을 주제별로 집계한 순위에서 뽑았다.
 * 프리오더가 26건(1/3)이라 앞 세 자리를 가져간다.
 *
 * 쓰는 규칙(PERSONA §1):
 * - 단정문. 설득·권유 문형을 쓰지 않는다.
 * - "season" 은 백오피스 용어다. 고객에게는 batch · stock · state 로 말한다.
 * - em dash 를 문장부호로 쓰지 않는다. 수동태로 행위자를 감추지 않는다.
 * - 사용법 질문은 여기 없다. 매뉴얼·Works with 로 보낸다(PERSONA 규칙 3).
 *
 * 아직 결정되지 않아 일부러 쓰지 않은 것(SITEMAP_PLAN §23.5): 카드 브랜드 목록(Amex),
 * 재입고 알림 수단, 발송 순서 기준, 주소 변경 마감 시점, 대리점 유무. 정해지면 여기에 문장을 더한다.
 */
export const faqList: FaqItemType[] = [
	{
		no: '01',
		group: 'Pre-order',
		question: 'What is a pre-order?',
		answer: [
			'We build in batches. A pre-order takes your order before the batch is built.',
			'You pay the full amount at checkout. Production starts when the batch closes, and we ship from Korea within the next four weeks.',
		],
		refs: [{ label: 'Store Policy', href: '/store-policy' }],
	},
	{
		no: '02',
		group: 'Pre-order',
		question: 'When will my pre-order ship?',
		answer: [
			'Production starts when a batch sells out or its order period ends. We ship from Korea within the next four weeks. How long it takes to reach you depends on your country.',
			'Units from one batch leave on different days. You get a tracking number by email when yours ships.',
		],
		refs: [{ label: 'My Order', href: '/customer/order' }],
	},
	{
		no: '03',
		group: 'Pre-order',
		question: 'Can I cancel a pre-order, or return a unit I received?',
		answer: [
			'Cancel any time before your unit ships, including while it is in production. We refund the full amount.',
			'After delivery, email cs@judahs.io within 30 days for a return authorization. The unit comes back unused in its original packaging, and return shipping is yours.',
		],
		refs: [
			{ label: 'Store Policy', href: '/store-policy' },
			{ label: 'cs@judahs.io', href: 'mailto:cs@judahs.io' },
		],
	},
	{
		no: '04',
		group: 'Stock',
		question: 'It says sold out. When does the next batch open?',
		answer: [
			'Each batch is a fixed quantity. When the next one has a date, the shop shows it.',
			'Each batch carries its own price.',
		],
		refs: [{ label: 'Shop', href: '/shop' }],
	},
	{
		no: '05',
		group: 'Payment',
		question: 'What payment methods do you take?',
		answer: [
			'Prices are in USD. Checkout takes PayPal and credit or debit cards.',
			'PayPal does not operate in every country. Where it does not, the card path stays open.',
		],
	},
	{
		no: '06',
		group: 'Shipping',
		question: 'Do you ship to my country?',
		answer: [
			'We ship from Busan, South Korea, by FedEx. Checkout shows shipping for the address you enter.',
			'The carrier gives real-time tracking. Transit times differ by destination, so we do not promise a delivery date.',
		],
	},
	{
		no: '07',
		group: 'Taxes',
		question: 'Are import duties and VAT included in the price?',
		answer: [
			'Import duties are included in the price. We pay them, so the carrier does not bill you for duties.',
			'Any VAT or GST your country charges is collected on delivery.',
		],
		refs: [{ label: 'Store Policy', href: '/store-policy' }],
	},
	{
		no: '08',
		group: 'Orders',
		question: 'How do I check my order?',
		answer: [
			'Sign in and open My Order. It lists your orders and their state.',
			'We email a confirmation when the order goes through, and a tracking number when it ships. If an order does not appear, send the order number to cs@judahs.io.',
		],
		refs: [
			{ label: 'My Order', href: '/customer/order' },
			{ label: 'cs@judahs.io', href: 'mailto:cs@judahs.io' },
		],
	},
	{
		no: '09',
		group: 'Warranty',
		question: 'My unit is faulty. What is covered?',
		answer: [
			'Every Judahs product carries a one year warranty against manufacturing defects, for the original purchaser. It does not cover misuse, unauthorized modification, or accidental damage.',
			'Email cs@judahs.io with proof of purchase. We repair units outside warranty for a fee.',
		],
		refs: [
			{ label: 'cs@judahs.io', href: 'mailto:cs@judahs.io' },
			{ label: 'Store Policy', href: '/store-policy' },
		],
	},
	{
		no: '10',
		group: 'Software',
		question: 'Where do I get firmware and software?',
		answer: [
			'Judahs Studio installs firmware and edits presets. The Software page carries the download and the release notes.',
			'Some firmware versions need the one before them. Follow the order in the release notes.',
		],
		refs: [
			{ label: 'Judahs Studio', href: '/software/judahs-studio' },
			{ label: 'Manuals', href: '/manuals' },
		],
	},
]

/** 10행 뒤에 서는 마감 행. 질문이 아니라 다음 자리를 가리킨다 */
export const faqMore: { group: string; body: string; refs: FaqRefType[] } = {
	group: 'More',
	body: 'How a product works is in its manual. The devices it controls are in Works with. Anything this page does not answer goes to cs@judahs.io.',
	refs: [
		{ label: 'Manuals', href: '/manuals' },
		{ label: 'Works with', href: '/works-with' },
		{ label: 'Contact', href: '/contact' },
	],
}
